// Cross-reference linkifiers used by the CCL and USML mirror pages.
//
// Two flavours:
//   * `linkifyEccns(html, { self })` — wrap every "3A001", "9A515", … in
//     an <a> pointing at /ccl/e/<ECCN>. Self-mentions are skipped.
//   * `linkifyUsml(html)` — wrap every "USML Category XV", "USML XV",
//     "USML XV(a)", or "Category XV of the U.S. Munitions List" in an
//     <a> pointing at the corresponding USML navigator page.
//
// Cross-navigator links (CCL→USML and USML→CCL) open in a NEW TAB so the
// reader keeps their place. Same-navigator links (an ECCN referenced
// inside another ECCN) stay in the current tab — normal browsing.
//
// Both helpers are HTML-safe by construction: they never touch text
// inside an existing <a> or attribute value, only free text between
// element boundaries.

const LINK_CLASS = "text-brass-deep border-b border-brass/40 hover:text-brass hover:border-brass transition-colors";
// Roman numerals for the 21 USML categories, longest-first so "XV" wins
// against "V" in a greedy alternation.
const USML_ROMANS = "XXI|XX|XIX|XVIII|XVII|XVI|XV|XIV|XIII|XII|XI|X|IX|VIII|VII|VI|V|IV|III|II|I";

// Wrap ECCN mentions in <a href="/ccl/e/…">. When called from a CCL entry
// page, pass the current ECCN as `self` to skip wrapping self-references.
// `newTab=true` when called from a USML page (cross-navigator link).
export function linkifyEccns(html, { self, newTab = false } = {}) {
  const attrs = newTab
    ? `target="_blank" rel="noopener noreferrer" class="${LINK_CLASS}"`
    : `class="${LINK_CLASS}"`;
  return replaceOutsideTags(html, /\b([0-9][A-E]\d{3})\b/g, (m) => {
    if (self && m === self) return m;
    return `<a href="/ccl/e/${m}" data-eccn-link="${m}" ${attrs}>${m}</a>`;
  });
}

// Wrap USML mentions in <a href="/usml/…">. Always cross-navigator so
// always opens in a new tab.
export function linkifyUsml(html) {
  const newTabAttrs = `target="_blank" rel="noopener noreferrer" class="${LINK_CLASS}"`;

  // 1) "USML Category XV(a)" / "USML XV(a)" — jump to paragraph page.
  const withPara = new RegExp(`\\bUSML(?:\\s+Category)?\\s+(${USML_ROMANS})\\s*\\(([a-x])\\)`, "g");
  html = replaceOutsideTags(html, withPara, (m, roman, letter) => {
    return `<a href="/usml/e/${roman}/${letter}" data-usml-link="${roman}(${letter})" ${newTabAttrs}>${m}</a>`;
  });

  // 2) "USML Category XV" / "USML XV" — jump to category page.
  const bare = new RegExp(`\\bUSML(?:\\s+Category)?\\s+(${USML_ROMANS})\\b`, "g");
  html = replaceOutsideTags(html, bare, (m, roman) => {
    return `<a href="/usml/${roman}" data-usml-link="${roman}" ${newTabAttrs}>${m}</a>`;
  });

  // 3) "Category XV of the U.S. Munitions List" — the long form.
  const longForm = new RegExp(`\\bCategory\\s+(${USML_ROMANS})\\s+of\\s+the\\s+U\\.?\\s*S\\.?\\s+Munitions\\s+List`, "g");
  html = replaceOutsideTags(html, longForm, (m, roman) => {
    return `<a href="/usml/${roman}" data-usml-link="${roman}" ${newTabAttrs}>${m}</a>`;
  });

  return html;
}

// Run `replace` only against text that sits BETWEEN tags — never against
// tag names, attribute values, or existing anchor text. Prevents double-
// wrapping or corrupting href="..." values.
function replaceOutsideTags(html, pattern, replacer) {
  const parts = html.split(/(<[^>]*>)/g);
  let insideAnchor = 0;
  for (let i = 0; i < parts.length; i++) {
    const p = parts[i];
    if (p.startsWith("<")) {
      if (/^<a\b/i.test(p)) insideAnchor++;
      else if (/^<\/a>/i.test(p)) insideAnchor = Math.max(0, insideAnchor - 1);
      continue;
    }
    if (insideAnchor > 0) continue;
    parts[i] = p.replace(pattern, replacer);
  }
  return parts.join("");
}
